import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, Alert, Table, ProgressBar, Accordion
} from 'react-bootstrap';
import {
    BarChart2, Plus, Send, CheckCircle, Clock,
    Star, MessageSquare, TrendingUp, Users,
    ChevronRight, Edit2, Trash2, RefreshCw,
    ThumbsUp, Smile, Meh, Frown, Target
} from 'lucide-react';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, RadialBarChart, RadialBar
} from 'recharts';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────
const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];

const TYPE_CONFIG = {
    pulse: { bg:'primary',  label:'Pulse',   icon:'💓' },
    enps:  { bg:'success',  label:'eNPS',    icon:'📊' },
    custom:{ bg:'info',     label:'Custom',  icon:'📝' },
};
const STATUS_CONFIG = {
    draft:  { bg:'secondary', label:'Draft'  },
    active: { bg:'success',   label:'Active' },
    closed: { bg:'primary',   label:'Closed' },
};
const QUESTION_TYPES = [
    { key:'rating',  label:'⭐ Rating Scale', desc:'1-to-N rating (1-5 or 1-10)' },
    { key:'nps',     label:'📊 NPS (0-10)',   desc:'Net Promoter Score question'   },
    { key:'text',    label:'📝 Open Text',    desc:'Free-text response'            },
    { key:'mcq',     label:'☑ Multiple Choice', desc:'Choose one option'          },
    { key:'boolean', label:'👍 Yes / No',     desc:'Simple yes or no question'     },
];
const TEMPLATE_LIST = [
    { key:'enps',                title:'Employee NPS (eNPS)', icon:'📊', desc:'Recommend-to-work question (0-10 NPS scale)' },
    { key:'monthly_pulse',       title:'Monthly Pulse Check', icon:'💓', desc:'5-question morale + wellbeing check' },
    { key:'manager_feedback',    title:'Manager Effectiveness',icon:'👔',desc:'Anonymous manager feedback (6 questions)' },
    { key:'onboarding_experience',title:'Onboarding Experience',icon:'🚀',desc:'For employees in first 30 days' },
    { key:'work_life_balance',   title:'Work-Life Balance',   icon:'⚖️', desc:'Burnout risk and flexibility assessment' },
];

const COLOURS = ['#4f46e5','#059669','#d97706','#dc2626','#0891b2','#7c3aed','#84cc16','#f43f5e'];

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
};

// eNPS health label
const enpsHealth = (score) => {
    if (score === null || score === undefined) return { label:'No data', color:'#6b7280', icon:<Meh size={18}/> };
    if (score >= 50) return { label:'Excellent', color:'#059669', icon:<ThumbsUp size={18}/> };
    if (score >= 30) return { label:'Healthy',   color:'#0891b2', icon:<Smile size={18}/> };
    if (score >= 0)  return { label:'Needs attention', color:'#d97706', icon:<Meh size={18}/> };
    return { label:'Critical', color:'#dc2626', icon:<Frown size={18}/> };
};

// Sentiment colour
const sentimentColour = (avg) => {
    if (!avg) return '#6b7280';
    if (avg >= 4.2) return '#059669';
    if (avg >= 3.0) return '#d97706';
    return '#dc2626';
};

// ════════════════════════════════════════════════════════════════════════════
const SurveysPage = () => {
    const { user }   = useAuth();
    const isHR       = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState(isHR ? 'dashboard' : 'pending');

    const tabs = [
        { key:'dashboard', label:'Dashboard',   icon:BarChart2,    show:isHR  },
        { key:'surveys',   label:'All Surveys', icon:MessageSquare,show:isHR  },
        { key:'enps',      label:'eNPS Trend',  icon:TrendingUp,   show:isHR  },
        { key:'pending',   label:'My Surveys',  icon:Star,         show:true  },
    ].filter(t => t.show);

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <MessageSquare size={22} className="me-2 text-primary"/>
                        Employee Engagement
                    </h4>
                    <p className="text-muted small mb-0">
                        Pulse surveys · eNPS · Sentiment analytics
                    </p>
                </div>
            </div>
            <div className="d-flex gap-1 flex-wrap mb-4">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab===t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
            </div>
            {tab === 'dashboard' && isHR && <DashboardTab/>}
            {tab === 'surveys'   && isHR && <SurveysTab/>}
            {tab === 'enps'      && isHR && <EnpsTrendTab/>}
            {tab === 'pending'   && <MyTab isHR={isHR}/>}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ════════════════════════════════════════════════════════════════════════════
const DashboardTab = () => {
    const [stats,   setStats]   = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axiosInstance.get('/surveys/stats')
            .then(r => setStats(r.data.data))
            .catch(() => toast.error('Failed to load stats'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!stats) return null;

    const enps   = stats.latest_enps;
    const health = enpsHealth(enps?.score);
    const sent   = stats.recent_sentiment;
    const part   = stats.participation || {};
    const sum    = stats.summary || {};

    return (
        <>
            {/* Top KPI row */}
            <Row className="g-3 mb-4">
                {/* eNPS card */}
                <Col xs={12} md={4}>
                    <Card className="border-0 shadow-sm h-100 p-4">
                        <div className="d-flex justify-content-between mb-3">
                            <div>
                                <div className="text-muted small fw-bold text-uppercase">Latest eNPS</div>
                                <div style={{ fontSize: 48, fontWeight: 800,
                                    color: health.color, lineHeight: 1.1 }}>
                                    {enps ? (enps.score >= 0 ? '+' : '') + enps.score : '—'}
                                </div>
                                <div className="d-flex align-items-center gap-1 mt-1"
                                    style={{ color: health.color }}>
                                    {health.icon}
                                    <span className="fw-bold small">{health.label}</span>
                                </div>
                            </div>
                            {enps && (
                                <div className="text-end">
                                    <div className="small text-muted mb-2">Breakdown</div>
                                    {[
                                        { label:'Promoters',  val:enps.promoters,  color:'#059669' },
                                        { label:'Passives',   val:enps.passives,   color:'#d97706' },
                                        { label:'Detractors', val:enps.detractors, color:'#dc2626' },
                                    ].map((r,i) => (
                                        <div key={i} className="small d-flex justify-content-between gap-2">
                                            <span style={{ color: r.color }}>{r.label}</span>
                                            <span className="fw-bold">{r.val}</span>
                                        </div>
                                    ))}
                                    <div className="text-muted" style={{ fontSize:10, marginTop:4 }}>
                                        {enps.total} respondents
                                    </div>
                                </div>
                            )}
                        </div>
                        {!enps && (
                            <p className="text-muted small">
                                No eNPS surveys closed yet. Run an eNPS survey to see your score here.
                            </p>
                        )}
                    </Card>
                </Col>

                {/* Sentiment card */}
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100 p-4 text-center">
                        <div className="text-muted small mb-2">Avg Sentiment</div>
                        <div style={{ fontSize:32, fontWeight:700,
                            color: sentimentColour(sent?.avg_rating) }}>
                            {sent?.avg_rating ? parseFloat(sent.avg_rating).toFixed(1) : '—'}
                        </div>
                        <div className="text-muted" style={{ fontSize:11 }}>/ 5.0</div>
                        <div className="mt-2">
                            <Badge bg={
                                sent?.label === 'positive' ? 'success' :
                                sent?.label === 'neutral'  ? 'warning' : 'danger'
                            } style={{ fontSize:11 }}>
                                {sent?.label || 'No data'}
                            </Badge>
                        </div>
                        <div className="text-muted" style={{ fontSize:10, marginTop:4 }}>
                            Last 90 days
                        </div>
                    </Card>
                </Col>

                {/* Participation card */}
                <Col xs={6} md={2}>
                    <Card className="border-0 shadow-sm h-100 p-4 text-center">
                        <div className="text-muted small mb-2">Response Rate</div>
                        <div style={{ fontSize:32, fontWeight:700, color:'#4f46e5' }}>
                            {part.response_rate || 0}%
                        </div>
                        <div className="text-muted" style={{ fontSize:11 }}>
                            {part.total_responded||0}/{part.total_assigned||0}
                        </div>
                        <ProgressBar now={part.response_rate||0}
                            style={{ height:6, marginTop:8 }} variant="primary"/>
                    </Card>
                </Col>

                {/* Survey counts */}
                <Col xs={12} md={4}>
                    <Card className="border-0 shadow-sm h-100 p-4">
                        <div className="text-muted small fw-bold mb-3">Survey Activity</div>
                        <Row className="g-2">
                            {[
                                { label:'Active',  val: sum.active  || 0, color:'#059669' },
                                { label:'Drafts',  val: sum.drafts  || 0, color:'#6b7280' },
                                { label:'Closed',  val: sum.closed  || 0, color:'#4f46e5' },
                                { label:'Total',   val: sum.total_surveys || 0, color:'#d97706' },
                            ].map((k, i) => (
                                <Col key={i} xs={6}>
                                    <div className="p-2 rounded text-center"
                                        style={{ background:'#f8fafc' }}>
                                        <div style={{ fontSize:22, fontWeight:700, color:k.color }}>
                                            {k.val}
                                        </div>
                                        <div className="text-muted" style={{ fontSize:11 }}>{k.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* eNPS explanation */}
            <Card className="border-0 shadow-sm p-4">
                <div className="fw-bold mb-2">How is eNPS calculated?</div>
                <div className="d-flex gap-4 flex-wrap" style={{ fontSize:13 }}>
                    {[
                        { range:'9–10', label:'Promoters',  color:'#059669', desc:'Enthusiastic advocates' },
                        { range:'7–8',  label:'Passives',   color:'#d97706', desc:'Satisfied but not enthusiastic' },
                        { range:'0–6',  label:'Detractors', color:'#dc2626', desc:'Unhappy, may discourage others' },
                    ].map((r,i) => (
                        <div key={i} className="d-flex align-items-start gap-2">
                            <div style={{ width:12, height:12, borderRadius:3, background:r.color,
                                flexShrink:0, marginTop:3 }}/>
                            <div>
                                <div className="fw-bold">{r.label} <span className="text-muted fw-normal">({r.range})</span></div>
                                <div className="text-muted">{r.desc}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="text-muted mt-2" style={{ fontSize:12 }}>
                    eNPS = % Promoters − % Detractors. Range: −100 to +100. &gt;+30 is healthy. &gt;+50 is excellent.
                </div>
            </Card>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// ALL SURVEYS TAB
// ════════════════════════════════════════════════════════════════════════════
const SurveysTab = () => {
    const [surveys,  setSurveys]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [showNew,  setShowNew]  = useState(false);
    const [selected, setSelected] = useState(null); // results view

    const load = () => {
        setLoading(true);
        axiosInstance.get('/surveys')
            .then(r => setSurveys(r.data.data || []))
            .catch(() => toast.error('Failed to load'))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const publish = async (id) => {
        try {
            const res = await axiosInstance.post(`/surveys/${id}/publish`);
            toast.success(res.data.message);
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const close = async (id) => {
        if (!window.confirm('Close this survey? No more responses will be accepted.')) return;
        try {
            await axiosInstance.post(`/surveys/${id}/close`);
            toast.success('Survey closed');
            load();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const del = async (id) => {
        if (!window.confirm('Delete this draft survey?')) return;
        try {
            await axiosInstance.delete(`/surveys/${id}`);
            toast.success('Deleted'); load();
        } catch { toast.error('Failed'); }
    };

    if (selected) return (
        <ResultsView surveyId={selected} onBack={() => { setSelected(null); load(); }}/>
    );

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
                    <Plus size={14} className="me-1"/>Create Survey
                </Button>
            </div>
            {surveys.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <MessageSquare size={40} className="mx-auto mb-3 opacity-50"/>
                    <p>No surveys yet. Create one from scratch or use a template.</p>
                    <Button variant="primary" size="sm" onClick={() => setShowNew(true)}>
                        <Plus size={14} className="me-1"/>Create Survey
                    </Button>
                </Card>
            ) : (
                <Row className="g-3">
                    {surveys.map(s => (
                        <Col key={s.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <div className="d-flex justify-content-between mb-2">
                                    <div className="d-flex gap-1">
                                        <Badge bg={TYPE_CONFIG[s.type]?.bg||'secondary'}>
                                            {TYPE_CONFIG[s.type]?.icon} {TYPE_CONFIG[s.type]?.label||s.type}
                                        </Badge>
                                        {s.is_anonymous && (
                                            <Badge bg="light" text="dark" style={{ fontSize:10 }}>🔒 Anon</Badge>
                                        )}
                                    </div>
                                    <Badge bg={STATUS_CONFIG[s.status]?.bg||'secondary'}>
                                        {STATUS_CONFIG[s.status]?.label}
                                    </Badge>
                                </div>
                                <h6 className="fw-bold mb-2">{s.title}</h6>
                                <div className="text-muted small mb-3 d-flex gap-3">
                                    <span>📋 {s.question_count} questions</span>
                                    <span>👥 {s.assigned_count} assigned</span>
                                </div>
                                {s.status !== 'draft' && s.assigned_count > 0 && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span className="text-muted">Response rate</span>
                                            <span className="fw-bold">{s.response_rate}%</span>
                                        </div>
                                        <ProgressBar now={s.response_rate||0} style={{ height:6 }}
                                            variant={s.response_rate>=70?'success':s.response_rate>=40?'warning':'danger'}/>
                                        <div className="text-muted" style={{ fontSize:10, marginTop:2 }}>
                                            {s.responded_count}/{s.assigned_count} responded
                                        </div>
                                    </div>
                                )}
                                {s.closes_at && (
                                    <div className="text-muted small mb-3">
                                        <Clock size={11} className="me-1"/>
                                        Closes {fmtDate(s.closes_at)}
                                    </div>
                                )}
                                <div className="d-flex gap-1 mt-auto">
                                    {s.status === 'draft' && (
                                        <>
                                            <Button size="sm" variant="success"
                                                className="flex-grow-1"
                                                onClick={() => publish(s.id)}>
                                                <Send size={12} className="me-1"/>Publish
                                            </Button>
                                            <Button size="sm" variant="outline-danger"
                                                className="py-0"
                                                onClick={() => del(s.id)}>
                                                <Trash2 size={12}/>
                                            </Button>
                                        </>
                                    )}
                                    {s.status === 'active' && (
                                        <>
                                            <Button size="sm" variant="outline-primary"
                                                className="flex-grow-1"
                                                onClick={() => setSelected(s.id)}>
                                                <BarChart2 size={12} className="me-1"/>Results
                                            </Button>
                                            <Button size="sm" variant="outline-secondary"
                                                onClick={() => close(s.id)}>
                                                Close
                                            </Button>
                                        </>
                                    )}
                                    {s.status === 'closed' && (
                                        <Button size="sm" variant="outline-primary"
                                            className="flex-grow-1"
                                            onClick={() => setSelected(s.id)}>
                                            <BarChart2 size={12} className="me-1"/>View Results
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {showNew && (
                <NewSurveyModal
                    onClose={() => setShowNew(false)}
                    onDone={() => { setShowNew(false); load(); }}
                />
            )}
        </>
    );
};

// ── Results View ──────────────────────────────────────────────────────────────
const ResultsView = ({ surveyId, onBack }) => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axiosInstance.get(`/surveys/${surveyId}/results`)
            .then(r => setData(r.data.data))
            .catch(() => toast.error('Failed to load results'))
            .finally(() => setLoading(false));
    }, [surveyId]);

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    if (!data)   return null;

    const { survey, questions } = data;

    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>
                ← Back
            </Button>
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{ background:'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h5 className="fw-bold mb-1">{survey.title}</h5>
                        <div className="d-flex gap-2 flex-wrap">
                            <Badge bg={TYPE_CONFIG[survey.type]?.bg||'secondary'}>
                                {TYPE_CONFIG[survey.type]?.label}
                            </Badge>
                            <Badge bg={STATUS_CONFIG[survey.status]?.bg||'secondary'}>
                                {STATUS_CONFIG[survey.status]?.label}
                            </Badge>
                            {survey.is_anonymous && <Badge bg="light" text="dark">🔒 Anonymous</Badge>}
                        </div>
                    </div>
                    <div className="text-end">
                        <div style={{ fontSize:28, fontWeight:700, color:'#4f46e5' }}>
                            {survey.response_rate}%
                        </div>
                        <div className="text-muted small">response rate</div>
                        <div className="text-muted small">
                            {survey.responded}/{survey.assigned} responded
                        </div>
                    </div>
                </div>
            </Card>

            {questions.map((q, i) => (
                <Card key={q.id} className="border-0 shadow-sm mb-4 p-4">
                    <div className="fw-bold mb-3">
                        Q{i+1}. {q.question}
                        <Badge bg="light" text="dark" className="ms-2" style={{ fontSize:10 }}>
                            {q.response_count} answers
                        </Badge>
                    </div>

                    {q.type === 'nps' && q.enps && (
                        <div>
                            <Row className="g-3 mb-3">
                                <Col xs={6} md={3}>
                                    <div className="text-center p-3 rounded"
                                        style={{ background:'#f0fdf4' }}>
                                        <div style={{ fontSize:32, fontWeight:800, color:'#059669' }}>
                                            {q.enps.score !== null
                                                ? (q.enps.score >= 0 ? '+' : '') + q.enps.score
                                                : '—'}
                                        </div>
                                        <div className="text-muted small">eNPS Score</div>
                                    </div>
                                </Col>
                                {[
                                    { label:'Promoters (9-10)', val:q.enps.promoters, pct:q.enps.promoter_pct, color:'#059669' },
                                    { label:'Passives (7-8)',   val:q.enps.passives,  pct:null,                color:'#d97706' },
                                    { label:'Detractors (0-6)', val:q.enps.detractors,pct:q.enps.detractor_pct,color:'#dc2626' },
                                ].map((r,j) => (
                                    <Col key={j} xs={4} md={3}>
                                        <div className="text-center p-3 rounded"
                                            style={{ background:'#f8fafc' }}>
                                            <div style={{ fontSize:22, fontWeight:700, color:r.color }}>
                                                {r.val}
                                            </div>
                                            <div className="text-muted" style={{ fontSize:11 }}>{r.label}</div>
                                            {r.pct !== null && (
                                                <div style={{ fontSize:10, color:r.color }}>{r.pct}%</div>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                            {q.distribution?.length > 0 && (
                                <ResponsiveContainer width="100%" height={160}>
                                    <BarChart data={q.distribution}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="score" tick={{ fontSize:11 }}/>
                                        <YAxis allowDecimals={false} tick={{ fontSize:11 }}/>
                                        <Tooltip/>
                                        <Bar dataKey="count" name="Responses">
                                            {q.distribution.map((d, idx) => (
                                                <Cell key={idx}
                                                    fill={d.score >= 9 ? '#059669' : d.score >= 7 ? '#d97706' : '#dc2626'}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    )}

                    {q.type === 'rating' && (
                        <div>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <div style={{ fontSize:36, fontWeight:800,
                                    color: sentimentColour(q.avg_score) }}>
                                    {q.avg_score?.toFixed ? q.avg_score.toFixed(1) : '—'}
                                </div>
                                <div>
                                    <div className="text-muted small">/ {q.scale}</div>
                                    <Badge bg={
                                        q.sentiment === 'positive' ? 'success' :
                                        q.sentiment === 'neutral'  ? 'warning' : 'danger'
                                    }>{q.sentiment}</Badge>
                                </div>
                            </div>
                            {q.distribution?.length > 0 && (
                                <ResponsiveContainer width="100%" height={140}>
                                    <BarChart data={q.distribution}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="score" tick={{ fontSize:11 }}/>
                                        <YAxis allowDecimals={false} tick={{ fontSize:11 }}/>
                                        <Tooltip/>
                                        <Bar dataKey="count" fill="#4f46e5" radius={[4,4,0,0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    )}

                    {q.type === 'mcq' && q.option_counts?.length > 0 && (
                        <div>
                            {q.option_counts.map((o, idx) => {
                                const pct = q.response_count ? Math.round((o.count/q.response_count)*100) : 0;
                                return (
                                    <div key={idx} className="mb-2">
                                        <div className="d-flex justify-content-between small mb-1">
                                            <span>{o.answer_value}</span>
                                            <span className="fw-bold">{o.count} ({pct}%)</span>
                                        </div>
                                        <ProgressBar now={pct} style={{ height:8 }}
                                            variant={['primary','success','info','warning'][idx%4]}/>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {q.type === 'boolean' && q.boolean_counts?.length > 0 && (
                        <div className="d-flex gap-4">
                            {q.boolean_counts.map((b, idx) => {
                                const pct = q.response_count ? Math.round((b.count/q.response_count)*100) : 0;
                                return (
                                    <div key={idx} className="text-center p-3 rounded"
                                        style={{ background:'#f8fafc', minWidth:80 }}>
                                        <div style={{ fontSize:24, fontWeight:700,
                                            color: b.answer_value==='true'?'#059669':'#dc2626' }}>
                                            {b.count}
                                        </div>
                                        <div className="fw-bold">
                                            {b.answer_value === 'true' ? '👍 Yes' : '👎 No'}
                                        </div>
                                        <div className="text-muted small">{pct}%</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {q.type === 'text' && (
                        <div>
                            {(q.text_responses || []).length === 0 ? (
                                <p className="text-muted small">No text responses yet.</p>
                            ) : (
                                <div style={{ maxHeight:280, overflowY:'auto' }}>
                                    {q.text_responses.map((t, idx) => (
                                        <div key={idx} className="mb-2 p-3 rounded"
                                            style={{ background:'#f8fafc', fontSize:13 }}>
                                            {t.respondent && (
                                                <div className="fw-medium small mb-1">{t.respondent}</div>
                                            )}
                                            <div>{t.answer_value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// eNPS TREND TAB
// ════════════════════════════════════════════════════════════════════════════
const EnpsTrendTab = () => {
    const [trend,   setTrend]   = useState([]);
    const [current, setCurrent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            axiosInstance.get('/surveys/enps/current'),
            axiosInstance.get('/surveys/enps/trend'),
        ]).then(([cur, tr]) => {
            setCurrent(cur.data.data);
            setTrend(tr.data.data || []);
        }).catch(() => {})
          .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;

    const health = enpsHealth(current?.score);

    return (
        <>
            {current ? (
                <Row className="g-4 mb-4">
                    <Col md={4}>
                        <Card className="border-0 shadow-sm h-100 p-4 text-center">
                            <div className="text-muted small fw-bold mb-2">CURRENT eNPS</div>
                            <div style={{ fontSize:64, fontWeight:900, color:health.color, lineHeight:1 }}>
                                {current.score >= 0 ? '+' : ''}{current.score}
                            </div>
                            <div className="d-flex align-items-center justify-content-center gap-1 mt-2"
                                style={{ color: health.color }}>
                                {health.icon}
                                <span className="fw-bold">{health.label}</span>
                            </div>
                            <div className="text-muted small mt-2">{current.survey_title}</div>
                            <Row className="g-2 mt-3">
                                {[
                                    { l:'Promoters',  v:current.promoters,  c:'#059669' },
                                    { l:'Passives',   v:current.passives,   c:'#d97706' },
                                    { l:'Detractors', v:current.detractors, c:'#dc2626' },
                                ].map((r,i) => (
                                    <Col key={i} xs={4}>
                                        <div className="p-2 rounded" style={{ background:'#f8fafc' }}>
                                            <div style={{ fontSize:18, fontWeight:700, color:r.c }}>{r.v}</div>
                                            <div style={{ fontSize:10 }} className="text-muted">{r.l}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>
                    <Col md={8}>
                        <Card className="border-0 shadow-sm h-100 p-4">
                            <div className="fw-bold mb-3">eNPS Score Over Time</div>
                            {trend.length < 2 ? (
                                <div className="text-muted small">
                                    Run more eNPS surveys to see the trend over time.
                                    ({trend.length} data point{trend.length !== 1 ? 's' : ''} so far)
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={trend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                        <XAxis dataKey="survey_title"
                                            tick={{ fontSize:10 }}
                                            tickFormatter={v => v.slice(0,12)+'…'}/>
                                        <YAxis domain={[-100,100]} tick={{ fontSize:11 }}/>
                                        <Tooltip formatter={(v) => [v >= 0 ? '+'+v : v, 'eNPS']}/>
                                        <Line type="monotone" dataKey="score" stroke="#4f46e5"
                                            strokeWidth={2} dot={{ fill:'#4f46e5', r:5 }}/>
                                        {/* Reference lines for health zones */}
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </Card>
                    </Col>
                </Row>
            ) : (
                <Card className="border-0 shadow-sm p-5 text-center text-muted mb-4">
                    <Target size={40} className="mx-auto mb-2 opacity-50"/>
                    <p>No eNPS data yet. Create and run an eNPS survey to see results here.</p>
                </Card>
            )}

            {/* Benchmarks */}
            <Card className="border-0 shadow-sm p-4">
                <div className="fw-bold mb-3">eNPS Benchmark Guide</div>
                <Row className="g-3">
                    {[
                        { range:'+50 to +100', label:'Excellent',        desc:'World-class employee experience. Top 10% of companies.', color:'#059669', bg:'#f0fdf4' },
                        { range:'+30 to +49',  label:'Healthy',          desc:'Strong engagement. Employees are happy and loyal.',      color:'#0891b2', bg:'#f0f9ff' },
                        { range:'0 to +29',    label:'Needs Attention',  desc:'Room for improvement. Many employees may be passive.',   color:'#d97706', bg:'#fffbeb' },
                        { range:'-100 to -1',  label:'Critical',         desc:'Significant issues. Immediate action required.',         color:'#dc2626', bg:'#fff1f2' },
                    ].map((b, i) => (
                        <Col key={i} xs={12} md={6}>
                            <div className="p-3 rounded" style={{ background:b.bg }}>
                                <div className="d-flex justify-content-between mb-1">
                                    <span className="fw-bold" style={{ color:b.color }}>{b.label}</span>
                                    <Badge bg="light" text="dark">{b.range}</Badge>
                                </div>
                                <div className="text-muted small">{b.desc}</div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MY SURVEYS TAB (Employee view)
// ════════════════════════════════════════════════════════════════════════════
const MyTab = ({ isHR }) => {
    const [pending,   setPending]   = useState([]);
    const [completed, setCompleted] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [taking,    setTaking]    = useState(null); // survey being taken

    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/surveys/my/pending'),
            axiosInstance.get('/surveys/my/completed'),
        ]).then(([p, c]) => {
            setPending(p.data.data || []);
            setCompleted(c.data.data || []);
        }).catch(() => {})
          .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;

    if (taking) return (
        <TakeSurveyView
            surveyId={taking}
            onBack={() => setTaking(null)}
            onDone={() => { setTaking(null); load(); }}
        />
    );

    return (
        <>
            {pending.length > 0 && (
                <Alert variant="warning" className="small py-2 mb-3">
                    <Clock size={14} className="me-1"/>
                    You have <strong>{pending.length}</strong> survey{pending.length !== 1 ? 's' : ''} pending. Your responses help improve the workplace!
                </Alert>
            )}

            {pending.length > 0 && (
                <>
                    <div className="fw-bold mb-3">Pending ({pending.length})</div>
                    <Row className="g-3 mb-4">
                        {pending.map(s => (
                            <Col key={s.id} xs={12} md={6} lg={4}>
                                <Card className="border-0 shadow-sm h-100 p-4"
                                    style={{ borderLeft:'4px solid #4f46e5' }}>
                                    <div className="d-flex justify-content-between mb-2">
                                        <Badge bg={TYPE_CONFIG[s.type]?.bg||'secondary'}>
                                            {TYPE_CONFIG[s.type]?.icon} {TYPE_CONFIG[s.type]?.label}
                                        </Badge>
                                        {s.is_anonymous && (
                                            <Badge bg="light" text="dark" style={{ fontSize:10 }}>🔒 Anonymous</Badge>
                                        )}
                                    </div>
                                    <h6 className="fw-bold mb-2">{s.title}</h6>
                                    {s.description && (
                                        <p className="text-muted small mb-3">{s.description}</p>
                                    )}
                                    <div className="text-muted small mb-3">
                                        📋 {s.question_count} questions
                                        {s.closes_at && ` · Closes ${fmtDate(s.closes_at)}`}
                                    </div>
                                    {s.is_anonymous && (
                                        <div className="text-muted small mb-3"
                                            style={{ fontSize:11 }}>
                                            Your responses are completely anonymous.
                                            HR cannot see who gave which answer.
                                        </div>
                                    )}
                                    <Button variant="primary" size="sm" className="mt-auto"
                                        onClick={() => setTaking(s.id)}>
                                        Start Survey <ChevronRight size={14}/>
                                    </Button>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </>
            )}

            {completed.length > 0 && (
                <>
                    <div className="fw-bold mb-3 text-muted">Completed ({completed.length})</div>
                    <Card className="border-0 shadow-sm">
                        <Table hover responsive className="mb-0 small align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                    <th className="ps-4">Survey</th>
                                    <th>Type</th>
                                    <th className="pe-4">Completed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {completed.map(s => (
                                    <tr key={s.id}>
                                        <td className="ps-4 fw-medium">{s.title}</td>
                                        <td>
                                            <Badge bg={TYPE_CONFIG[s.type]?.bg||'secondary'}>
                                                {TYPE_CONFIG[s.type]?.label}
                                            </Badge>
                                        </td>
                                        <td className="pe-4 text-muted">
                                            <CheckCircle size={13} className="me-1 text-success"/>
                                            {fmtDate(s.submitted_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </>
            )}

            {pending.length === 0 && completed.length === 0 && (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <CheckCircle size={40} className="mx-auto mb-2 text-success opacity-75"/>
                    <p>No surveys pending. Check back later!</p>
                </Card>
            )}
        </>
    );
};

// ── Take Survey View ──────────────────────────────────────────────────────────
const TakeSurveyView = ({ surveyId, onBack, onDone }) => {
    const [survey,  setSurvey]  = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(0); // one question at a time

    useEffect(() => {
        axiosInstance.get(`/surveys/${surveyId}`)
            .then(r => setSurvey(r.data.data))
            .catch(() => toast.error('Failed to load survey'))
            .finally(() => setLoading(false));
    }, [surveyId]);

    const setAnswer = (questionId, value, isNum = false) => {
        setAnswers(p => ({
            ...p,
            [questionId]: {
                question_id:  questionId,
                answer_value: isNum ? String(value) : value,
                answer_num:   isNum ? parseFloat(value) : null,
            },
        }));
    };

    const submit = async () => {
        const questions = survey.questions || [];
        // Validate required
        const missing = questions.filter(q => q.is_required && !answers[q.id]);
        if (missing.length) {
            toast.error(`Please answer all required questions (${missing.length} remaining)`);
            return;
        }
        setSubmitting(true);
        try {
            await axiosInstance.post(`/surveys/${surveyId}/respond`, {
                answers: Object.values(answers),
            });
            toast.success('Survey submitted! Thank you for your feedback.');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit'); }
        finally { setSubmitting(false); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    if (!survey) return null;

    const questions = survey.questions || [];
    const answered  = Object.keys(answers).length;
    const progress  = questions.length ? Math.round((answered / questions.length) * 100) : 0;

    return (
        <div>
            <Button variant="outline-secondary" size="sm" className="mb-3" onClick={onBack}>
                ← Back
            </Button>
            <Card className="border-0 shadow-sm mb-4 p-4"
                style={{ background:'linear-gradient(135deg,#eef2ff,#e0e7ff)' }}>
                <h5 className="fw-bold mb-1">{survey.title}</h5>
                {survey.description && (
                    <p className="text-muted small mb-2">{survey.description}</p>
                )}
                {survey.is_anonymous && (
                    <Alert variant="info" className="small py-1 mb-2">
                        🔒 Your responses are completely anonymous.
                    </Alert>
                )}
                <div className="d-flex justify-content-between small mb-1">
                    <span className="text-muted">Progress</span>
                    <span>{answered}/{questions.length} answered</span>
                </div>
                <ProgressBar now={progress} style={{ height:8 }}/>
            </Card>

            {questions.map((q, i) => (
                <Card key={q.id} className="border-0 shadow-sm mb-4 p-4">
                    <div className="fw-bold mb-3">
                        Q{i+1}. {q.question}
                        {q.is_required && <span className="text-danger ms-1">*</span>}
                    </div>

                    {/* Rating scale */}
                    {q.type === 'rating' && (
                        <div>
                            <div className="d-flex justify-content-between text-muted small mb-2">
                                <span>{q.min_label || '1'}</span>
                                <span>{q.max_label || q.scale}</span>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                {Array.from({ length: q.scale || 5 }, (_, j) => j+1).map(n => (
                                    <Button key={n} size="sm"
                                        variant={answers[q.id]?.answer_num === n ? 'primary' : 'outline-secondary'}
                                        style={{ minWidth:44, fontWeight:600 }}
                                        onClick={() => setAnswer(q.id, n, true)}>
                                        {n}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* NPS 0-10 */}
                    {q.type === 'nps' && (
                        <div>
                            <div className="d-flex justify-content-between text-muted small mb-2">
                                <span>0 — {q.min_label || 'Not at all likely'}</span>
                                <span>10 — {q.max_label || 'Extremely likely'}</span>
                            </div>
                            <div className="d-flex gap-1 flex-wrap">
                                {Array.from({ length: 11 }, (_, j) => j).map(n => (
                                    <Button key={n} size="sm"
                                        variant={answers[q.id]?.answer_num === n
                                            ? (n >= 9 ? 'success' : n >= 7 ? 'warning' : 'danger')
                                            : 'outline-secondary'}
                                        style={{ minWidth:40, fontWeight:600 }}
                                        onClick={() => setAnswer(q.id, n, true)}>
                                        {n}
                                    </Button>
                                ))}
                            </div>
                            <div className="d-flex justify-content-between mt-1" style={{ fontSize:10 }}>
                                <span className="text-danger">Detractor (0-6)</span>
                                <span className="text-warning">Passive (7-8)</span>
                                <span className="text-success">Promoter (9-10)</span>
                            </div>
                        </div>
                    )}

                    {/* MCQ */}
                    {q.type === 'mcq' && q.options && (
                        <div className="d-flex flex-column gap-2">
                            {q.options.map((opt, j) => (
                                <div key={j}
                                    className="p-3 rounded border"
                                    style={{
                                        cursor:'pointer',
                                        background: answers[q.id]?.answer_value === opt ? '#eef2ff' : undefined,
                                        borderColor: answers[q.id]?.answer_value === opt ? '#4f46e5' : '#dee2e6',
                                    }}
                                    onClick={() => setAnswer(q.id, opt, false)}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{
                                            width:18, height:18, borderRadius:'50%', border:'2px solid',
                                            borderColor: answers[q.id]?.answer_value === opt ? '#4f46e5' : '#dee2e6',
                                            background:  answers[q.id]?.answer_value === opt ? '#4f46e5' : 'white',
                                            flexShrink:0,
                                        }}/>
                                        <span>{opt}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Boolean */}
                    {q.type === 'boolean' && (
                        <div className="d-flex gap-3">
                            {[{ val:'true', label:'👍 Yes', color:'success' },
                              { val:'false', label:'👎 No',  color:'danger'  }].map(b => (
                                <Button key={b.val}
                                    variant={answers[q.id]?.answer_value === b.val
                                        ? b.color : `outline-${b.color}`}
                                    onClick={() => setAnswer(q.id, b.val, false)}
                                    style={{ minWidth:100 }}>
                                    {b.label}
                                </Button>
                            ))}
                        </div>
                    )}

                    {/* Text */}
                    {q.type === 'text' && (
                        <Form.Control as="textarea" rows={3}
                            placeholder="Your answer here…"
                            value={answers[q.id]?.answer_value || ''}
                            onChange={e => setAnswer(q.id, e.target.value, false)}/>
                    )}

                    {answers[q.id] && (
                        <div className="mt-2">
                            <CheckCircle size={14} className="text-success me-1"/>
                            <span className="text-success small">Answered</span>
                        </div>
                    )}
                </Card>
            ))}

            <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted small">
                    {Object.keys(answers).length}/{questions.length} questions answered
                </div>
                <Button variant="primary" size="lg" onClick={submit} disabled={submitting}>
                    {submitting ? <Spinner size="sm"/> : '✓ Submit Survey'}
                </Button>
            </div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// NEW SURVEY MODAL
// ════════════════════════════════════════════════════════════════════════════
const NewSurveyModal = ({ onClose, onDone }) => {
    const [mode, setMode]     = useState('template'); // 'template' | 'custom'
    const [form, setForm]     = useState({
        title:'', description:'', type:'pulse',
        is_anonymous: true, frequency:'one_time',
        closes_at:'', target_dept_id:'',
    });
    const [departments, setDepts] = useState([]);
    const [questions,   setQs]    = useState([]);
    const [saving, setSaving]     = useState(false);

    useEffect(() => {
        axiosInstance.get('/hr/departments').then(r => setDepts(r.data.data||[])).catch(()=>{});
    }, []);

    const fromTemplate = async (key) => {
        setSaving(true);
        try {
            const res = await axiosInstance.post(`/surveys/from-template/${key}`, {
                title:         form.title || undefined,
                closes_at:     form.closes_at || undefined,
                target_dept_id:form.target_dept_id || undefined,
                frequency:     form.frequency,
            });
            toast.success(`Survey created from template! ${res.data.data?.question_count} questions added.`);
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const createCustom = async () => {
        if (!form.title.trim()) return toast.error('Survey title is required');
        setSaving(true);
        try {
            const res = await axiosInstance.post('/surveys', form);
            const surveyId = res.data.data?.id;

            // Add questions
            for (const q of questions) {
                if (!q.question.trim()) continue;
                await axiosInstance.post(`/surveys/${surveyId}/questions`, q);
            }
            toast.success('Survey created!');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    const addQuestion = () => {
        setQs(p => [...p, {
            question:'', type:'rating', scale:5, is_required:true,
            min_label:'', max_label:'', options:[], sort_order: p.length,
        }]);
    };

    return (
        <Modal show onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <Plus size={18} className="me-2 text-primary"/>Create Survey
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex gap-2 mb-4">
                    <Button size="sm" variant={mode==='template'?'primary':'outline-secondary'}
                        onClick={() => setMode('template')}>
                        📋 Use Template
                    </Button>
                    <Button size="sm" variant={mode==='custom'?'primary':'outline-secondary'}
                        onClick={() => setMode('custom')}>
                        ✏️ Build Custom
                    </Button>
                </div>

                {/* Common fields */}
                <Row className="g-3 mb-4">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Survey Title (optional override)</Form.Label>
                        <Form.Control placeholder="Leave blank to use template title"
                            value={form.title}
                            onChange={e => setForm(p => ({...p, title: e.target.value}))}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Closes On</Form.Label>
                        <Form.Control type="datetime-local" value={form.closes_at}
                            onChange={e => setForm(p => ({...p, closes_at: e.target.value}))}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Target Department</Form.Label>
                        <Form.Select value={form.target_dept_id}
                            onChange={e => setForm(p => ({...p, target_dept_id: e.target.value}))}>
                            <option value="">— All employees —</option>
                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Form.Select>
                    </Col>
                </Row>

                {mode === 'template' ? (
                    <div>
                        <div className="fw-bold mb-3">Choose a template:</div>
                        <Row className="g-3">
                            {TEMPLATE_LIST.map(t => (
                                <Col key={t.key} xs={12} md={6}>
                                    <div className="p-3 rounded border"
                                        style={{ cursor:'pointer', borderColor:'#dee2e6' }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor='#4f46e5'}
                                        onMouseLeave={e => e.currentTarget.style.borderColor='#dee2e6'}>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div className="fw-bold small">
                                                    {t.icon} {t.title}
                                                </div>
                                                <div className="text-muted" style={{ fontSize:12 }}>
                                                    {t.desc}
                                                </div>
                                            </div>
                                            <Button size="sm" variant="primary"
                                                disabled={saving}
                                                onClick={() => fromTemplate(t.key)}>
                                                {saving ? <Spinner size="sm"/> : 'Use'}
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                ) : (
                    <div>
                        <Row className="g-3 mb-4">
                            <Col xs={4}>
                                <Form.Label className="small fw-bold">Type</Form.Label>
                                <Form.Select value={form.type}
                                    onChange={e => setForm(p => ({...p, type: e.target.value}))}>
                                    <option value="pulse">💓 Pulse</option>
                                    <option value="enps">📊 eNPS</option>
                                    <option value="custom">📝 Custom</option>
                                </Form.Select>
                            </Col>
                            <Col xs={4}>
                                <Form.Label className="small fw-bold">Frequency</Form.Label>
                                <Form.Select value={form.frequency}
                                    onChange={e => setForm(p => ({...p, frequency: e.target.value}))}>
                                    {['one_time','weekly','bi_weekly','monthly','quarterly'].map(f =>
                                        <option key={f} value={f}>{f.replace('_',' ')}</option>)}
                                </Form.Select>
                            </Col>
                            <Col xs={4} className="d-flex align-items-end">
                                <Form.Check type="switch" label="Anonymous responses"
                                    checked={form.is_anonymous}
                                    onChange={e => setForm(p => ({...p, is_anonymous: e.target.checked}))}/>
                            </Col>
                        </Row>

                        {/* Questions builder */}
                        <div className="fw-bold mb-2">Questions</div>
                        {questions.map((q, i) => (
                            <div key={i} className="p-3 mb-2 rounded"
                                style={{ background:'#f8fafc', border:'1px solid #e5e7eb' }}>
                                <Row className="g-2">
                                    <Col xs={9}>
                                        <Form.Control placeholder={`Q${i+1}. Your question here…`}
                                            value={q.question}
                                            onChange={e => setQs(p => p.map((x,j) =>
                                                j===i ? {...x, question:e.target.value} : x))}/>
                                    </Col>
                                    <Col xs={2}>
                                        <Form.Select value={q.type}
                                            onChange={e => setQs(p => p.map((x,j) =>
                                                j===i ? {...x, type:e.target.value} : x))}>
                                            {QUESTION_TYPES.map(t =>
                                                <option key={t.key} value={t.key}>{t.label}</option>)}
                                        </Form.Select>
                                    </Col>
                                    <Col xs={1} className="d-flex align-items-center justify-content-center">
                                        <Button size="sm" variant="outline-danger" className="py-0"
                                            onClick={() => setQs(p => p.filter((_,j) => j!==i))}>
                                            ✕
                                        </Button>
                                    </Col>
                                    {q.type === 'mcq' && (
                                        <Col xs={12}>
                                            <Form.Control size="sm"
                                                placeholder="Options, comma-separated (e.g. Never,Sometimes,Always)"
                                                value={q.options?.join(',') || ''}
                                                onChange={e => setQs(p => p.map((x,j) =>
                                                    j===i ? {...x, options:e.target.value.split(',').map(v=>v.trim())} : x))}/>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        ))}
                        <Button size="sm" variant="outline-primary" onClick={addQuestion}>
                            <Plus size={13} className="me-1"/>Add Question
                        </Button>

                        <div className="d-flex gap-2 mt-4">
                            <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                            <Button variant="primary" className="flex-grow-1 fw-bold"
                                onClick={createCustom} disabled={saving}>
                                {saving ? <Spinner size="sm"/> : 'Create Survey'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SurveysPage;
